import React, { useState, useMemo } from 'react';
import { MagnifyingGlassIcon, PlusIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import QuickBooksVendorReport from './QuickBooksVendorReport.jsx';

const IntegrationsTab = ({ getIntegrations, IntegrationCard, handleInstall, onUpvote, upvotedIntegrations = new Set(), upvoteCounts = {} }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // all, connected, coming soon

  // Get existing integrations from the system
  const existingIntegrations = getIntegrations ? getIntegrations() : [];
  
  // Helper function to generate logo.dev URL
  const getLogoUrl = (domain) => {
    // Extract domain from various URL formats or use domain directly
    let cleanDomain = domain;
    if (domain.includes('://')) {
      try {
        const url = new URL(domain);
        cleanDomain = url.hostname.replace('www.', '');
      } catch {
        // If URL parsing fails, try to extract domain manually
        cleanDomain = domain.replace(/https?:\/\/(www\.)?/, '').split('/')[0];
      }
    }
    return `https://img.logo.dev/${cleanDomain}?token=live_6a1a28fd-6420-4492-aeb0-b297461d9de2&size=64&retina=true`;
  };

  // Comprehensive list of integrations based on the images
  const allIntegrations = [
    // Personal/Connected Integrations (from first image)
    { key: 'gmail', name: 'Gmail', logoUrl: 'https://www.gstatic.com/images/branding/product/1x/gmail_48dp.png', status: 'connected', category: 'personal' },

    // Financial/Accounting Integrations
    { key: 'quickbooks', name: 'QuickBooks', logoUrl: getLogoUrl('quickbookstraining.com'), status: 'beta', category: 'available' },
    { key: 'xero', name: 'Xero', logoUrl: getLogoUrl('xero.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'plaid', name: 'Plaid (Bank Accounts)', logoUrl: getLogoUrl('plaid.com'), status: 'coming-soon', category: 'coming-soon' },
    
    // Coming Soon Integrations (from all images)
    { key: 'notion', name: 'Notion', logoUrl: getLogoUrl('notion.so'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'linear', name: 'Linear', logoUrl: getLogoUrl('linear.app'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'gdocs', name: 'Google Docs', logoUrl: 'https://www.gstatic.com/images/branding/product/1x/docs_48dp.png', status: 'coming-soon', category: 'coming-soon' },
    { key: 'supabase', name: 'Supabase', logoUrl: getLogoUrl('supabase.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'square', name: 'Square', logoUrl: getLogoUrl('square.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'paypal', name: 'PayPal', logoUrl: getLogoUrl('paypal.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'calendar', name: 'Google Calendar', logoUrl: 'https://www.gstatic.com/images/branding/product/1x/calendar_48dp.png', status: 'coming-soon', category: 'coming-soon' },
    { key: 'airtable', name: 'Airtable', logoUrl: getLogoUrl('airtable.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'gsheet', name: 'Google Sheets', logoUrl: 'https://www.gstatic.com/images/branding/product/1x/sheets_48dp.png', status: 'coming-soon', category: 'coming-soon' },
    { key: 'github', name: 'GitHub', logoUrl: getLogoUrl('github.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'gdrive', name: 'Google Drive', logoUrl: 'https://ssl.gstatic.com/docs/doclist/images/drive_2022q3_32dp.png', status: 'coming-soon', category: 'coming-soon' },
    { key: 'webflow', name: 'Webflow', logoUrl: getLogoUrl('webflow.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'slack', name: 'Slack', logoUrl: getLogoUrl('slack.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'gtasks', name: 'Google Tasks', logoUrl: 'https://www.gstatic.com/images/branding/product/1x/tasks_48dp.png', status: 'coming-soon', category: 'coming-soon' },
    { key: 'greenhouse', name: 'Greenhouse', logoUrl: getLogoUrl('greenhouse.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'invideo', name: 'Invideo', logoUrl: getLogoUrl('invideo.io'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'mysql', name: 'MySQL', logoUrl: getLogoUrl('mysql.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'outlook', name: 'Outlook', logoUrl: getLogoUrl('outlook.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'reddit', name: 'Reddit', logoUrl: getLogoUrl('reddit.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'salesforce', name: 'Salesforce', logoUrl: getLogoUrl('salesforce.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'snowflake', name: 'Snowflake', logoUrl: getLogoUrl('snowflake.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'youtube', name: 'YouTube', logoUrl: getLogoUrl('youtube.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'gorgias', name: 'Gorgias', logoUrl: getLogoUrl('gorgias.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'hubspot', name: 'HubSpot', logoUrl: getLogoUrl('hubspot.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'monday', name: 'Monday', logoUrl: getLogoUrl('monday.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'onedrive', name: 'OneDrive', logoUrl: getLogoUrl('onedrive.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'pipedrive', name: 'Pipedrive', logoUrl: getLogoUrl('pipedrive.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'resend', name: 'Resend', logoUrl: getLogoUrl('resend.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'shopify', name: 'Shopify', logoUrl: getLogoUrl('shopify.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'whatsapp', name: 'WhatsApp Business', logoUrl: getLogoUrl('whatsapp.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'zendesk', name: 'Zendesk', logoUrl: getLogoUrl('zendesk.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'asana', name: 'Asana', logoUrl: getLogoUrl('asana.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'attio', name: 'Attio', logoUrl: getLogoUrl('attio.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'jira', name: 'Jira', logoUrl: getLogoUrl('atlassian.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'coda', name: 'Coda', logoUrl: getLogoUrl('coda.io'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'bigquery', name: 'BigQuery', logoUrl: getLogoUrl('cloud.google.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'dropbox', name: 'Dropbox', logoUrl: getLogoUrl('dropbox.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'figma', name: 'Figma', logoUrl: getLogoUrl('figma.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'fireflies', name: 'Fireflies', logoUrl: getLogoUrl('fireflies.ai'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'sentry', name: 'Sentry', logoUrl: getLogoUrl('sentry.io'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'confluence', name: 'Confluence', logoUrl: getLogoUrl('atlassian.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'postgres', name: 'Postgres', logoUrl: getLogoUrl('postgresql.org'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'affinity', name: 'Affinity', logoUrl: getLogoUrl('affinity.co'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'clickup', name: 'ClickUp', logoUrl: getLogoUrl('clickup.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'facebook', name: 'Facebook', logoUrl: getLogoUrl('facebook.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'firebase', name: 'Firebase', logoUrl: getLogoUrl('firebase.google.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'meet', name: 'Google Meet', logoUrl: 'https://fonts.gstatic.com/s/i/productlogos/meet_2020q4/v1/web-32dp/logo_meet_2020q4_color_1x_web_32dp.png', status: 'coming-soon', category: 'coming-soon' },
    
    // Beta integrations (available but in beta)
    { key: 'apollo', name: 'Apollo', logoUrl: getLogoUrl('apollo.io'), status: 'beta', category: 'coming-soon' },
    { key: 'gong', name: 'Gong', logoUrl: getLogoUrl('gong.io'), status: 'beta', category: 'coming-soon' },
    { key: 'cal', name: 'Cal.com', logoUrl: getLogoUrl('cal.com'), status: 'beta', category: 'coming-soon' },
    { key: 'calendly', name: 'Calendly', logoUrl: getLogoUrl('calendly.com'), status: 'beta', category: 'coming-soon' },
    { key: 'vercel', name: 'Vercel', logoUrl: getLogoUrl('vercel.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'bitbucket', name: 'Bit Bucket', logoUrl: getLogoUrl('bitbucket.org'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'intercom', name: 'Intercom', logoUrl: getLogoUrl('intercom.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'zoom', name: 'Zoom', logoUrl: getLogoUrl('zoom.us'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'stripe', name: 'Stripe', logoUrl: getLogoUrl('stripe.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'canva', name: 'Canva', logoUrl: getLogoUrl('canva.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'discord', name: 'Discord', logoUrl: getLogoUrl('discord.com'), status: 'coming-soon', category: 'coming-soon' },
    { key: 'mixpanel', name: 'Mixpanel', logoUrl: getLogoUrl('mixpanel.com'), status: 'coming-soon', category: 'coming-soon' },
  ];

  // Merge existing integrations with new ones, preserving existing connection status
  const mergedIntegrations = useMemo(() => {
    const existingMap = new Map();
    existingIntegrations.forEach(integration => {
      existingMap.set(integration.key, integration);
    });

    return allIntegrations.map(integration => {
      const existing = existingMap.get(integration.key);
      if (existing) {
        // Use existing integration data but keep the new status/category if not present
        return {
          ...integration,
          ...existing,
          status: integration.status || existing.status || 'coming-soon',
          category: integration.category || existing.category || 'coming-soon'
        };
      }
      return integration;
    });
  }, [existingIntegrations, allIntegrations]);

  // Filter integrations based on search and category
  const filteredIntegrations = useMemo(() => {
    let filtered = mergedIntegrations;
    
    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(integration => 
        integration.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Filter by category
    if (activeFilter === 'connected') {
      filtered = filtered.filter(integration => integration.category === 'connected' || integration.status === 'connected');
    } else if (activeFilter === 'coming soon') {
      filtered = filtered.filter(integration => integration.category === 'coming-soon' || integration.category === 'coming soon');
    } else if (activeFilter === 'available') {
      filtered = filtered.filter(integration => integration.category === 'available');
    }
    
    return filtered;
  }, [searchQuery, activeFilter, mergedIntegrations]);

  // Group integrations by category
  const personalIntegrations = filteredIntegrations.filter(i => i.category === 'personal');
  const comingSoonIntegrations = filteredIntegrations.filter(i => 
    i.category === 'coming-soon' || i.category === 'coming soon'
  );
  const connectedIntegrations = filteredIntegrations.filter(i => 
    i.category === 'connected' || i.status === 'connected'
  );
  const availableIntegrations = filteredIntegrations.filter(i => 
    i.category === 'available'
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'connected':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Connected</span>;
      case 'beta':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Beta</span>;
      case 'coming-soon':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Coming soon</span>;
      default:
        return null;
    }
  };

  const handleIntegrationClick = (integration) => {
    // Allow clicking on all integrations - users can upvote coming soon ones
    // Only prevent installation if status is coming-soon
    if (integration.status === 'coming-soon') {
      // Still allow upvoting, just don't trigger installation
      return;
    }
    
    // Convert to the format expected by handleInstall
    const installIntegration = {
      key: integration.key,
      name: integration.name,
      logoUrl: integration.logoUrl,
      requiresAuth: integration.requiresAuth || false,
      authConfigId: integration.authConfigId
    };
    
    handleInstall(installIntegration);
  };

  return (
    <div className="pt-12">
      {/* Header with Search and Add Button */}
      <div className="flex items-center justify-between mb-8">
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
        {/* <button className="ml-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          <PlusIcon className="h-4 w-4 mr-2" />
          Add MCP Server
        </button> */}
      </div>

      {/* Filter Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveFilter('all')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeFilter === 'all'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setActiveFilter('connected')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeFilter === 'connected'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Connected
          </button>
          <button
            onClick={() => setActiveFilter('coming soon')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeFilter === 'coming soon'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Coming Soon
          </button>
        </nav>
      </div>

      {/* Personal Integrations Section */}
      {personalIntegrations.length > 0 && (
        <div className="mb-12">
          <h3 className="text-lg font-medium text-gray-900 mb-6">Personal Integrations</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {personalIntegrations.map(integration => {
              const isUpvoted = upvotedIntegrations.has(integration.name);
              const upvoteCount = upvoteCounts[integration.name] || 0;
              
              return (
                <div
                  key={integration.key}
                  className="relative cursor-pointer rounded-lg border border-gray-200 bg-transparent p-4 transition-colors hover:border-gray-300"
                >
                  <div 
                    className="flex items-center space-x-3"
                    onClick={() => handleIntegrationClick(integration)}
                  >
                    <img
                      src={integration.logoUrl}
                      alt={integration.name}
                      className="h-8 w-8 object-contain rounded"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `data:image/svg+xml;base64,${btoa(`
                          <svg width="32" height="32" xmlns="http://www.w3.org/2000/svg">
                            <rect width="32" height="32" fill="#f3f4f6" rx="4"/>
                            <text x="16" y="20" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="#6b7280">${integration.name.charAt(0).toUpperCase()}</text>
                          </svg>
                        `)}`;
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{integration.name}</p>
                    </div>
                    {getStatusBadge(integration.status)}
                    
                    {/* Upvote Button - Moved to far right */}
                    {onUpvote && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onUpvote(integration.name);
                        }}
                        className={`flex items-center gap-1 px-2 py-1 rounded border transition-colors ${
                          isUpvoted
                            ? 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100'
                            : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50'
                        }`}
                        title={isUpvoted ? 'Remove upvote' : 'Upvote this integration'}
                      >
                        <ChevronUpIcon className={`h-4 w-4 ${isUpvoted ? 'text-blue-600' : 'text-gray-500'}`} />
                        <span className="text-xs font-medium">{upvoteCount}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* QuickBooks Vendor Report Section */}
      {connectedIntegrations.some(integration => integration.key === 'quickbooks') && (
        <div className="mb-8">
          <QuickBooksVendorReport />
        </div>
      )}

      {/* Available Integrations Section */}
      {availableIntegrations.length > 0 && (
        <div className="mb-12">
          <h3 className="text-lg font-medium text-gray-900 mb-6">Available Integrations</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {availableIntegrations.map(integration => {
              const isUpvoted = upvotedIntegrations.has(integration.name);
              const upvoteCount = upvoteCounts[integration.name] || 0;
              
              return (
                <div
                  key={integration.key}
                  className="relative cursor-pointer rounded-lg border border-gray-200 bg-transparent p-4 transition-colors hover:border-gray-300"
                >
                  <div 
                    className="flex items-center space-x-3"
                    onClick={() => handleIntegrationClick(integration)}
                  >
                    <img
                      src={integration.logoUrl}
                      alt={integration.name}
                      className="h-8 w-8 object-contain rounded"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `data:image/svg+xml;base64,${btoa(`
                          <svg width="32" height="32" xmlns="http://www.w3.org/2000/svg">
                            <rect width="32" height="32" fill="#f3f4f6" rx="4"/>
                            <text x="16" y="20" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="#6b7280">${integration.name.charAt(0).toUpperCase()}</text>
                          </svg>
                        `)}`;
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{integration.name}</p>
                    </div>
                    {getStatusBadge(integration.status)}
                    
                    {/* Upvote Button - Moved to far right */}
                    {onUpvote && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onUpvote(integration.name);
                        }}
                        className={`flex items-center gap-1 px-2 py-1 rounded border transition-colors ${
                          isUpvoted
                            ? 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100'
                            : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50'
                        }`}
                        title={isUpvoted ? 'Remove upvote' : 'Upvote this integration'}
                      >
                        <ChevronUpIcon className={`h-4 w-4 ${isUpvoted ? 'text-blue-600' : 'text-gray-500'}`} />
                        <span className="text-xs font-medium">{upvoteCount}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Coming Soon Integrations Section */}
      {comingSoonIntegrations.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-6">Coming Soon Integrations</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {comingSoonIntegrations.map(integration => {
              const isUpvoted = upvotedIntegrations.has(integration.name);
              const upvoteCount = upvoteCounts[integration.name] || 0;
              
              return (
                <div
                  key={integration.key}
                  className="relative cursor-pointer rounded-lg border border-gray-200 bg-transparent p-4 transition-colors hover:border-gray-300"
                >
                  <div 
                    className="flex items-center space-x-3"
                    onClick={() => handleIntegrationClick(integration)}
                  >
                    <img
                      src={integration.logoUrl}
                      alt={integration.name}
                      className="h-8 w-8 object-contain rounded"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `data:image/svg+xml;base64,${btoa(`
                          <svg width="32" height="32" xmlns="http://www.w3.org/2000/svg">
                            <rect width="32" height="32" fill="#f3f4f6" rx="4"/>
                            <text x="16" y="20" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="#6b7280">${integration.name.charAt(0).toUpperCase()}</text>
                          </svg>
                        `)}`;
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{integration.name}</p>
                    </div>
                    {getStatusBadge(integration.status)}
                    
                    {/* Upvote Button - Moved to far right */}
                    {onUpvote && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onUpvote(integration.name);
                        }}
                        className={`flex items-center gap-1 px-2 py-1 rounded border transition-colors ${
                          isUpvoted
                            ? 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100'
                            : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50'
                        }`}
                        title={isUpvoted ? 'Remove upvote' : 'Upvote this integration'}
                      >
                        <ChevronUpIcon className={`h-4 w-4 ${isUpvoted ? 'text-blue-600' : 'text-gray-500'}`} />
                        <span className="text-xs font-medium">{upvoteCount}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No Results */}
      {filteredIntegrations.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-sm">No integrations found matching your search.</p>
        </div>
      )}
    </div>
  );
};

export default IntegrationsTab;
